/**
 * Wes LLM service — conversational routing in Wes Bright's voice.
 */

import { AgentLlmError, callAgentLlm } from './llmClient';
import type { WesVoiceMode } from './wes/wesPrompts';
import {
  defaultWesSystemPrompt,
  unpackWesSettings,
} from './wes/wesPrompts';
import type { AgentSettings } from './agentSettingsService';
import { isDefaultWesPromptBody } from './wes/wesPrompts';
import { HARRY_CAPABILITY_APPENDIX } from './harry/harryPrompts';
import { buildInstructionStack } from './ai/instructionStack';
import { WES_CARD } from './ai/agentCard';
import { serializePacket, type SurfacePacket } from './ai/contextPacket';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

export interface WesLlmResult {
  reply: string;
  capabilityIds: string[];
}

function parseWesJson(raw: string): WesLlmResult | null {
  const trimmed = raw.trim();
  const jsonBlock = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)?.[1] ?? trimmed;
  try {
    const parsed = JSON.parse(jsonBlock) as { reply?: unknown; capabilityIds?: unknown };
    const reply = typeof parsed.reply === 'string' ? parsed.reply.trim() : '';
    const capabilityIds = Array.isArray(parsed.capabilityIds)
      ? parsed.capabilityIds.filter((id): id is string => typeof id === 'string')
      : [];
    if (!reply) return null;
    return { reply, capabilityIds };
  } catch {
    return null;
  }
}

export function resolveWesSystemPrompt(config: AgentSettings | null, voiceMode: WesVoiceMode): string {
  if (!config?.systemPrompt?.trim()) {
    return defaultWesSystemPrompt(voiceMode);
  }
  const { promptBody } = unpackWesSettings(config.systemPrompt);
  return promptBody || defaultWesSystemPrompt(voiceMode);
}

export function resolveWesVoiceMode(config: AgentSettings | null): WesVoiceMode {
  if (!config?.systemPrompt?.trim()) return 'normal';
  return unpackWesSettings(config.systemPrompt).voiceMode;
}

/** Canon-aware stack for Wes (same layers as Harry; Wes card + persona). */
export function buildCanonAwareWesSystemPrompt(
  config: AgentSettings | null,
  voiceMode: WesVoiceMode,
  uiMode: 'b' | 'c' | null,
): string {
  const storedBody = config?.systemPrompt ? unpackWesSettings(config.systemPrompt).promptBody : '';
  const overlay = isDefaultWesPromptBody(storedBody) ? null : storedBody;
  return buildInstructionStack({
    card: WES_CARD,
    voiceMode,
    uiMode,
    workspaceOverlay: overlay,
    capabilityAppendix: HARRY_CAPABILITY_APPENDIX,
  }).systemPrompt;
}

export async function askWes(params: {
  config: AgentSettings;
  voiceMode: WesVoiceMode;
  userPrompt: string;
  workspaceId: string;
  canonAware?: boolean;
  packet?: SurfacePacket | null;
  uiMode?: 'b' | 'c' | null;
}): Promise<WesLlmResult> {
  const systemPrompt = params.canonAware
    ? buildCanonAwareWesSystemPrompt(params.config, params.voiceMode, params.uiMode ?? null)
    : resolveWesSystemPrompt(params.config, params.voiceMode);
  const userPrompt = params.canonAware && params.packet
    ? `${serializePacket(params.packet)}\n\n${params.userPrompt}`
    : params.userPrompt;
  const model = params.config.model?.trim() || DEFAULT_MODEL;

  const content = await callAgentLlm({
    userPrompt,
    systemPrompt,
    model,
    conversationId: `wes-${params.workspaceId}`,
    maxTokens: 900,
    temperature: params.voiceMode === 'wes' ? 0.65 : 0.35,
  });

  const parsed = parseWesJson(content);
  if (parsed) return parsed;

  return {
    reply: content.trim(),
    capabilityIds: [],
  };
}

export { defaultWesSystemPrompt, unpackWesSettings };
export type { WesVoiceMode };

export { AgentLlmError };
